import { apiClient } from '@/api/client';
import { Category, Filter } from '@/api/models';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

interface ImportOptionsProps {
  selectedCategoryIds: number[];
  selectedFilterIds: number[];
  selectedStatus: string;
  onCategoryChange: (ids: number[]) => void;
  onFilterChange: (ids: number[]) => void;
  onStatusChange: (status: string) => void;
}

export const ImportOptions: React.FC<ImportOptionsProps> = ({
  selectedCategoryIds,
  selectedFilterIds,
  selectedStatus,
  onCategoryChange,
  onFilterChange,
  onStatusChange,
}) => {
  const { data: categories = [] } = useQuery({
    queryKey: [queryKeys.ADMIN_CATEGORIES],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/categories');
      if (response.error) throw new Error('Failed to fetch categories');
      return (response.data as Category[]).filter(c => c.enabled);
    },
  });

  const { data: filters = [] } = useQuery({
    queryKey: [queryKeys.ADMIN_FILTERS],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/filters');
      if (response.error) throw new Error('Failed to fetch filters');
      return (response.data as Filter[]).filter(f => f.enabled);
    },
  });

  const categoryOptions = categories.map(c => ({
    value: c.id!,
    label: c.name,
  }));

  const filterOptions = filters.map(f => ({
    value: f.id!,
    label: f.name,
  }));

  return (
    <div className='space-y-6'>
      <div className='bg-gray-50 rounded-lg p-4'>
        <h3 className='text-sm font-medium text-gray-700 mb-4'>
          Default Status for Imported Businesses
        </h3>
        <div className='flex items-center space-x-4'>
          <label className='flex items-center'>
            <input
              type='radio'
              name='status'
              value='Pending'
              checked={selectedStatus === 'Pending'}
              onChange={e => onStatusChange(e.target.value)}
              className='mr-2'
            />
            <span className='text-sm text-gray-700'>Pending</span>
            <span className='ml-1 text-xs text-gray-500'>
              (Review before publishing)
            </span>
          </label>
          <label className='flex items-center'>
            <input
              type='radio'
              name='status'
              value='Active'
              checked={selectedStatus === 'Active'}
              onChange={e => onStatusChange(e.target.value)}
              className='mr-2'
            />
            <span className='text-sm text-gray-700'>Active</span>
            <span className='ml-1 text-xs text-gray-500'>
              (Publish immediately)
            </span>
          </label>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-6'>
        <div className='bg-gray-50 rounded-lg p-4'>
          <h3 className='text-sm font-medium text-gray-700 mb-3'>
            Default Categories
          </h3>
          <p className='text-xs text-gray-500 mb-3'>
            Select categories to assign to all imported businesses
          </p>
          <CheckboxGroup
            options={categoryOptions}
            selectedValues={selectedCategoryIds}
            onChange={onCategoryChange}
            columns={2}
          />
          {categoryOptions.length === 0 && (
            <p className='text-sm text-gray-400'>No categories available</p>
          )}
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h3 className='text-sm font-medium text-gray-700 mb-3'>
            Default Filters (Amenities)
          </h3>
          <p className='text-xs text-gray-500 mb-3'>
            Select filters to assign to all imported businesses
          </p>
          <CheckboxGroup
            options={filterOptions}
            selectedValues={selectedFilterIds}
            onChange={onFilterChange}
            columns={2}
          />
          {filterOptions.length === 0 && (
            <p className='text-sm text-gray-400'>No filters available</p>
          )}
        </div>
      </div>
    </div>
  );
};
